/**
 * The raster gate, which is not the vector gate.
 *
 * The board's bars for raster are F1 >= 0.90 on clean images and >= 0.80 on
 * noisy ones, for lines and circles. The vector gate's 0.98 precision and recall
 * would be wrong here in both directions: too strict to ever pass on a scan, and
 * beside the point, because what matters for a scan is the balance of the two
 * rather than either alone.
 *
 * Clean and noisy are held to separate bars and never averaged. An average lets a
 * detector that is excellent on clean paper and useless on a real scan report a
 * comfortable middle figure, and a real scan is the whole reason this exists.
 */
export const RASTER_GATE_F1_CLEAN = 0.9;
export const RASTER_GATE_F1_NOISY = 0.8;

/** Published accuracy for scanned-drawing recognition, printed beside every figure. */
export const PUBLISHED_RASTER_F1_RANGE = [0.54, 0.66] as const;

/**
 * The raster report's output directory, and **the third thing in this repository
 * called a version.**
 *
 * `docs/HANDOFF.md` already warns that `PROFILE_VERSION` in `artefacts/build.ts`
 * drives the vector report's directory while `DEFAULT_MATCHER_PROFILE.version`
 * drives the comparison's, and that bumping the wrong one moves a report. This is
 * a third, deliberately named for the surface it belongs to: it moves
 * `output/drawing2d-raster/<version>/` and nothing else.
 */
export const RASTER_PROFILE_VERSION = '0.1.0';

export function f1(precision: number, recall: number): number {
  if (precision + recall === 0) return 0;
  return (2 * precision * recall) / (precision + recall);
}

export interface RasterGateInput {
  variant: 'clean' | 'noisy';
  type: string;
  precision: number;
  recall: number;
}

export function evaluateRasterGate(rows: readonly RasterGateInput[]): {
  pass: boolean;
  failures: string[];
} {
  // A gate with nothing to score is a gate that cannot fail, which is worse than
  // a failing gate. The comparison runner shipped in exactly that state once.
  if (rows.length === 0) {
    return { pass: false, failures: ['no rows were scored, so this gate proves nothing'] };
  }

  const failures: string[] = [];
  for (const row of rows) {
    const bar = row.variant === 'clean' ? RASTER_GATE_F1_CLEAN : RASTER_GATE_F1_NOISY;
    const score = f1(row.precision, row.recall);
    if (score < bar) {
      failures.push(
        `${row.variant} ${row.type}: F1 ${score.toFixed(3)} is below the bar of ${bar.toFixed(3)}`,
      );
    }
  }
  return { pass: failures.length === 0, failures };
}
